import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  ApiError,
  Severity,
  Source,
  Status,
  type ComplianceDetail,
  type DocumentSummary,
  type ReminderRule,
} from '../api/client'
import { invalidateFleet } from '../api/invalidate'
import { requireAccessToken } from '../auth/msal'
import { ComplianceChip } from '../components/ComplianceChip'
import { ComplianceEditor } from '../components/ComplianceEditor'
import { ObligationDocuments } from '../components/ObligationDocuments'
import { VehicleFields } from '../components/VehicleFields'
import { RtoSummary } from '../components/RtoSummary'
import { ReminderEditor } from '../components/ReminderEditor'
import { VehicleReminders } from '../components/VehicleReminders'
import { CopyableRegistration } from '../components/CopyableRegistration'
import { ShareVehicle } from '../components/ShareVehicle'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
// The edit overlay reuses the .modal shell defined alongside the advanced filter.
// Imported explicitly so the styles are present on this route in dev, where CSS
// is injected per module rather than bundled into one file.
import '../components/AdvancedFilter.css'
import './VehicleDetail.css'

/**
 * One vehicle, as a full page at /vehicles/:registration.
 *
 * The registration number is the URL key rather than the numeric id: it is the
 * business identity of the record, so the address is readable and can be typed or
 * pasted by a person. The numeric id stays an internal detail.
 *
 * Editing a single date remains a modal. Navigating away and back to change one
 * expiry would be heavier than the change itself, and it is genuinely modal in the
 * "answer this one question" sense.
 */
export function VehicleDetail() {
  const { registration = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<'compliance' | 'activity' | 'reminders'>('compliance')

  /*
    Which obligation's bell is open, if any.

    The bell and the Reminders tab drive the SAME editor over the same rule.
    Two forms over one row of data would drift - one would grow a field the
    other lacks - and the drift only shows up as a rule behaving unlike the one
    beside it.
  */
  const [remindingTypeId, setRemindingTypeId] = useState<number | null>(null)
  const [editingTypeId, setEditingTypeId] = useState<number | null>(null)
  // One obligation's documents open at a time. Expanding a second collapses the
  // first, so the list stays a list rather than growing into a wall of forms.
  const [openDocsTypeId, setOpenDocsTypeId] = useState<number | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [sharing, setSharing] = useState(false)

  // Only while an overlay is open — the page itself must scroll normally.
  useBodyScrollLock(editingTypeId !== null || sharing)

  // Two hops: registration -> id, then id -> detail. It keeps the API surface as
  // it is, and the first result is cached, so returning to a vehicle costs one
  // request rather than two.
  const lookup = useQuery({
    queryKey: ['vehicle-lookup', registration],
    queryFn: () => api.findByRegistration(registration),
  })

  const vehicleId = lookup.data?.vehicleId

  const vehicle = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () => api.getVehicle(vehicleId!),
    enabled: vehicleId != null,
  })

  const activity = useQuery({
    queryKey: ['activity', vehicleId],
    queryFn: () => api.getActivity(vehicleId!),
    enabled: vehicleId != null && tab === 'activity',
  })

  /*
    This vehicle's own reminders AND the fleet rules that already cover it.

    Loaded on the compliance tab as well as the reminders one, because the bell
    on each obligation has to know whether a rule already exists before it is
    pressed - an outline bell that turns out to be an edit is a worse surprise
    than one extra query.
  */
  const reminders = useQuery({
    queryKey: ['reminders-vehicle', vehicleId],
    queryFn: () => api.getRemindersForVehicle(vehicleId!),
    enabled: vehicleId != null,
  })

  // Stable identity for the memo below: `?? []` inline is a new array every
  // render, which would rebuild the map on every keystroke elsewhere on the page.
  const rulesData = reminders.data ?? EMPTY_RULES

  /*
    This vehicle's OWN rules, by obligation.

    Inherited fleet rules are deliberately excluded from the bell's filled state:
    the bell opens an editor for a rule this vehicle carries, and a fleet rule is
    not one - pressing it would silently create a second rule and produce two
    emails. The Reminders tab is where the inherited ones are shown and explained.
  */
  const ownRulesByType = useMemo(() => {
    const map = new Map<number, (typeof rulesData)[number]>()
    for (const r of rulesData) {
      if (r.isOwnRule && r.complianceTypeId != null) map.set(r.complianceTypeId, r)
    }
    return map
  }, [rulesData])

  const ownReminderCount = ownRulesByType.size

  const registry = useQuery({
    queryKey: ['registry-status'],
    queryFn: () => api.getRegistryStatus(),
    staleTime: 10 * 60 * 1000,
  })

  // Which obligations carry a certificate comes from acceptsDocument in the
  // reference data, so nothing here hardcodes the five. Cached hard — this
  // changes roughly never, and the API caches it for 30 minutes anyway.
  const reference = useQuery({
    queryKey: ['reference'],
    queryFn: () => api.getReference(),
    staleTime: 30 * 60 * 1000,
  })

  // One request for the whole vehicle rather than one per obligation: the rows
  // filter it locally, so expanding a row costs nothing.
  const documents = useQuery({
    queryKey: ['documents', vehicleId],
    queryFn: () => api.getDocuments(vehicleId!),
    enabled: vehicleId != null,
  })

  const acceptsDocument = new Set(
    (reference.data?.complianceTypes ?? [])
      .filter((t) => t.acceptsDocument)
      .map((t) => t.complianceTypeId),
  )

  const v = vehicle.data

  /**
   * When the registry last confirmed anything about this vehicle.
   *
   * Taken as the LATEST LastVerifiedUtc across the obligations the registry
   * actually supplied, rather than a field on the vehicle. There is no single
   * "vehicle was synced" moment: a sweep writes each obligation separately, and
   * an obligation the registry has nothing to say about is never stamped at all.
   * The most recent one is the honest answer to "how old is this?".
   *
   * Obligations from the sheet or typed by hand are excluded — their timestamp
   * says when a person or an import touched them, which is a different claim.
   */
  const lastRtoSync = useMemo(() => {
    const stamps = (v?.compliance ?? [])
      .filter((c) => c.source === Source.RegistryApi && c.lastVerifiedUtc)
      .map((c) => c.lastVerifiedUtc as string)

    return stamps.length === 0
      ? null
      : stamps.reduce((latest, s) => (s > latest ? s : latest))
  }, [v])

  /**
   * Back goes to the previous history entry so the register's filters, page and
   * scroll position are restored exactly. With nothing behind it — a deep link, a
   * pasted URL, a fresh tab — it goes to the bare register instead of stepping
   * out of the app.
   *
   * The test is the router's own history index, not location.key. `key` is
   * 'default' only for an untouched first entry, so any replace() before this
   * point mints a new key while the entry is still the first one — which is
   * exactly what arriving here from the add form's duplicate check does. Reading
   * the index gets that case right; reading the key sent the user to whatever
   * page they were on before this site.
   */
  function goBack() {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0

    if (index > 0) navigate(-1)
    else navigate('/register')
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Escape steps back one level: it closes whatever overlay is open, and only
      // leaves the page when nothing is stacked on top of it.
      if (sharing) setSharing(false)
      else if (editingTypeId !== null) setEditingTypeId(null)
      else goBack()
    }

    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  const remove = useMutation({
    /*
        Re-read the vehicle first, and delete with THAT row version.

        The version the page is holding goes stale the moment anything writes to
        the vehicle — an RTO sync filling its identity, a colleague editing a
        field, a fleet-wide identity pass. The delete then came back 409 and the
        screen said "someone else changed this vehicle first", which reads as a
        broken button when nobody else was involved at all: the user's own sync,
        a minute earlier, had moved it.

        The guard is kept rather than dropped. Re-reading narrows the window from
        "however long this page has been open" to the milliseconds between the
        read and the delete, which is what a concurrency guard is actually for.
    */
    mutationFn: async () => {
      await requireAccessToken()
      const fresh = await api.getVehicle(vehicleId!)
      return api.deleteVehicle(vehicleId!, fresh.rowVersion!)
    },
    onSuccess: async () => {
      await invalidateFleet(queryClient)
      navigate('/register', { replace: true })
    },
  })

  const sync = useMutation({
    // Same shape as the add form's: a click on a page that may have been open
    // long enough for the token to expire. Acquired here while the gesture is
    // still fresh, so a refresh popup is not blocked.
    mutationFn: async () => {
      await requireAccessToken()
      return api.syncFromRegistry(vehicleId!)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
      await invalidateFleet(queryClient)
    },
  })

  const deleteConflict = remove.error instanceof ApiError && remove.error.isConflict
  const editing = v?.compliance?.find((c) => c.complianceTypeId === editingTypeId)

  if (lookup.isPending) {
    return (
      <div className="detail">
        <p className="muted">Loading…</p>
      </div>
    )
  }

  if (!lookup.data) {
    return (
      <div className="detail">
        <header className="detail__head">
          <button type="button" className="detail__back" onClick={goBack}>
            ← Register
          </button>
        </header>
        <div className="empty">
          <p>
            No vehicle is registered as <b className="mono">{registration}</b>.
          </p>
          <p className="muted">Check the number, or add it from the register.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="detail">
      <header className="detail__head">
        <button type="button" className="detail__back" onClick={goBack}>
          ← Register
        </button>

        {v && (
          <button
            type="button"
            className="detail__share"
            onClick={() => setSharing(true)}
            title="Send this vehicle's compliance position to someone"
          >
            Share
          </button>
        )}

        <div className="detail__ident">
          <div className="eyebrow">Vehicle</div>
          <CopyableRegistration registration={v?.registration ?? registration} />
          {v && (
            <p className="detail__sub">
              {[v.vehicleType, [v.make, v.model].filter(Boolean).join(' '), v.site]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>
      </header>

      <nav className="detail__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'compliance'}
          onClick={() => setTab('compliance')}
        >
          Compliance
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'activity'}
          onClick={() => setTab('activity')}
        >
          History
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'reminders'}
          onClick={() => setTab('reminders')}
        >
          Reminders
          {/* Only this vehicle's own are counted. The inherited fleet rules are
              listed inside, but they are not something this vehicle carries. */}
          {ownReminderCount > 0 && <span className="detail__tabcount">{ownReminderCount}</span>}
        </button>
      </nav>

      {vehicle.isPending && <p className="muted">Loading…</p>}
      {vehicle.error && <p className="banner banner--error">{(vehicle.error as Error).message}</p>}

      {v && tab === 'compliance' && (
        <div className="detail__cols">
          <div className="detail__side">
            <VehicleFields vehicle={v} reference={reference.data} />

            <section className="syncbar">
              <div>
                <b>Sync from RTO</b>

                {/*
                  When the registry last confirmed this vehicle, stated before anything
                  else. Every date on the page is only as good as its age, and "synced
                  in March" changes how much weight a reader gives the rest of it.

                  Never-synced is spelled out rather than left blank: an empty space
                  reads as "nothing to say", and the truth is the opposite.
                */}
                <p className={`syncbar__when${lastRtoSync ? '' : ' syncbar__when--never'}`}>
                  {/* The same two marks the register uses, so one symbol means one
                      thing across both surfaces. */}
                  <span aria-hidden="true">{lastRtoSync ? '✓' : '⚠'}</span>
                  {lastRtoSync ? (
                    <>
                      Last RTO sync <b>{formatSyncDate(lastRtoSync)}</b>
                      <span className="syncbar__ago">{describeAge(lastRtoSync)}</span>
                    </>
                  ) : (
                    <b>Never synced with the RTO</b>
                  )}
                </p>

                <p>
                  {registry.data?.isConfigured
                    ? `Fetches current details from ${registry.data.providerName}. Values you corrected by hand are kept.`
                    : 'No registry provider is configured yet — the VAHAN aggregator subscription is still pending.'}
                </p>
              </div>
              <button
                type="button"
                className="btn"
                disabled={!registry.data?.isConfigured || sync.isPending}
                title={
                  registry.data?.isConfigured
                    ? undefined
                    : 'Requires a licensed VAHAN aggregator subscription'
                }
                onClick={() => sync.mutate()}
              >
                {sync.isPending ? 'Syncing…' : 'Sync now'}
              </button>
            </section>

            {sync.data && (
              <div className="editor__note">
                <p>
                  Synced: <b>{sync.data.valuesApplied}</b> obligations updated,{' '}
                  <b>{sync.data.valuesSkipped}</b> kept because they were corrected by hand
                  {/* Reported because it used to silently not happen: one press applied
                      the dates and left make, model, chassis and engine blank. */}
                  {sync.data.identityFieldsFilled ? (
                    <>
                      , and <b>{sync.data.identityFieldsFilled}</b> vehicle details filled
                    </>
                  ) : null}
                  .
                </p>

                {/*
                  What was kept rather than applied. A hand-edited field, or an identity
                  anchor the RTO disagrees with — the second especially, because silently
                  keeping is how a one-character chassis difference goes unnoticed.
                */}
                {sync.data.identityNotes && sync.data.identityNotes.length > 0 && (
                  <ul className="syncnotes">
                    {sync.data.identityNotes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {sync.error && <p className="editor__conflict">{(sync.error as Error).message}</p>}

            <RtoSummary vehicleId={vehicleId!} registration={v.registration!} />
          </div>

          <div className="detail__main">
            <section className="prov">
              <h4>
                <span>Compliance &amp; provenance</span>
                <span className="prov__count">
                  {v.compliance?.length ?? 0} obligations
                  {documents.data?.length ? ` · ${documents.data.length} documents` : ''}
                </span>
              </h4>
              {v.compliance?.map((c) => (
                <ComplianceRow
                  key={c.complianceTypeId}
                  vehicleId={vehicleId!}
                  detail={c}
                  onEdit={() => setEditingTypeId(c.complianceTypeId!)}
                  hasReminder={ownRulesByType.has(c.complianceTypeId!)}
                  reminderOpen={remindingTypeId === c.complianceTypeId}
                  onToggleReminder={() =>
                    setRemindingTypeId((was) =>
                      was === c.complianceTypeId ? null : c.complianceTypeId!,
                    )
                  }
                  reminderEditor={
                    remindingTypeId === c.complianceTypeId && v.registration ? (
                      <ReminderEditor
                        vehicleId={vehicleId!}
                        registration={v.registration}
                        complianceTypeId={c.complianceTypeId!}
                        obligationName={c.displayName ?? 'This obligation'}
                        existing={ownRulesByType.get(c.complianceTypeId!)}
                        onClose={() => setRemindingTypeId(null)}
                      />
                    ) : null
                  }
                  // Permit (4) and Goods Tax (7) carry no certificate, so they get
                  // no attach control at all — read from the data, not a list here.
                  acceptsDocument={acceptsDocument.has(c.complianceTypeId)}
                  documents={(documents.data ?? []).filter(
                    (d) => d.complianceTypeId === c.complianceTypeId,
                  )}
                  docsOpen={openDocsTypeId === c.complianceTypeId}
                  onToggleDocs={() =>
                    setOpenDocsTypeId((was) =>
                      was === c.complianceTypeId ? null : c.complianceTypeId!,
                    )
                  }
                />
              ))}
            </section>

            <section className="danger">
              {!confirmingDelete ? (
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete this vehicle
                </button>
              ) : (
                <div className="danger__confirm">
                  <p>
                    <b>Remove {v.registration} from the register?</b> The record and its full
                    history are retained for audit — it stops appearing in the register but
                    nothing is destroyed.
                  </p>

                  {deleteConflict && (
                    <p className="editor__conflict" role="alert">
                      Someone else changed this vehicle first. Reload and try again.
                    </p>
                  )}

                  {remove.error && !deleteConflict && (
                    <p className="editor__error" role="alert">
                      {(remove.error as Error).message}
                    </p>
                  )}

                  <div className="editor__actions">
                    <button
                      type="button"
                      className="btn btn--danger"
                      onClick={() => remove.mutate()}
                      disabled={remove.isPending}
                    >
                      {remove.isPending ? 'Removing…' : 'Yes, remove it'}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setConfirmingDelete(false)}
                      disabled={remove.isPending}
                    >
                      Keep it
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      )}

      {v && tab === 'reminders' && (
        <VehicleReminders
          vehicleId={vehicleId!}
          registration={v.registration!}
          rules={reminders.data ?? []}
          isPending={reminders.isPending}
          obligations={v.compliance ?? []}
        />
      )}

      {tab === 'activity' && (
        <section className="timeline">
          {activity.isPending && <p className="muted">Loading history…</p>}

          {activity.data?.items?.length === 0 && (
            <p className="muted">Nothing has been recorded against this vehicle yet.</p>
          )}

          {activity.data?.items?.map((entry) => (
            <article key={entry.activityLogId} className="event">
              <div className="event__top">
                <span className="event__action">{entry.action}</span>
                <time className="mono event__when">{formatWhen(entry.performedAtUtc)}</time>
              </div>

              <p className="event__what">
                {entry.complianceTypeName && <strong>{entry.complianceTypeName}: </strong>}
                {entry.oldValue && entry.newValue ? (
                  <>
                    changed from <span className="mono">{entry.oldValue}</span> to{' '}
                    <span className="mono">{entry.newValue}</span>
                  </>
                ) : (
                  (entry.notes ?? '—')
                )}
              </p>

              <div className="event__meta mono">
                <span>{entry.performedByName ?? entry.performedBy}</span>
                <span>·</span>
                <span>{describeSource(entry.source)}</span>
              </div>
            </article>
          ))}
        </section>
      )}

      {v && sharing && (
        <ShareVehicle
          vehicle={v}
          documents={documents.data ?? []}
          onClose={() => setSharing(false)}
        />
      )}

      {/* A single date is a three-second change, so it stays an overlay too. */}
      {v && editing && vehicleId != null && (
        <>
          <div className="scrim" onClick={() => setEditingTypeId(null)} aria-hidden="true" />
          <div className="modal modal--edit" role="dialog" aria-modal="true">
            <header className="modal__head">
              <h2>{editing.displayName}</h2>
              <button
                type="button"
                className="drawer__close"
                onClick={() => setEditingTypeId(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="modal__body">
              <ComplianceEditor
                vehicleId={vehicleId}
                detail={editing}
                onDone={() => setEditingTypeId(null)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * One obligation: its date, its provenance, and the scans behind it.
 *
 * The documents live in this row rather than in a panel of their own. They are
 * the evidence the date rests on — and where the expiry printed on the paper
 * disagrees with the expiry in the register, that disagreement is the finding.
 * A separate list would name every obligation twice and put the two halves of
 * one fact in different places.
 */
function ComplianceRow({
  vehicleId,
  detail,
  onEdit,
  acceptsDocument,
  documents,
  docsOpen,
  onToggleDocs,
  hasReminder,
  reminderOpen,
  onToggleReminder,
  reminderEditor,
}: {
  vehicleId: number
  detail: ComplianceDetail
  onEdit: () => void
  acceptsDocument: boolean
  documents: DocumentSummary[]
  docsOpen: boolean
  onToggleDocs: () => void
  /** This vehicle's own rule for this obligation — never an inherited fleet one. */
  hasReminder: boolean
  reminderOpen: boolean
  onToggleReminder: () => void
  /** Rendered by the parent, so one editor component serves the bell and the tab. */
  reminderEditor: React.ReactNode
}) {
  const cell = {
    status: detail.status,
    expiryDate: detail.expiryDate,
    severity: detail.severity ?? Severity.Unknown,
    daysUntilExpiry: detail.daysUntilExpiry,
  }

  return (
    <div className="prow">
      <div className="prow__top">
        <span className="prow__name">{detail.displayName}</span>
        <div className="prow__right">
          <ComplianceChip cell={cell} />

          {acceptsDocument && (
            <button
              type="button"
              className="prow__edit"
              onClick={onToggleDocs}
              aria-expanded={docsOpen}
              aria-label={
                documents.length === 0
                  ? `Attach a certificate for ${detail.displayName}`
                  : `${documents.length} certificates for ${detail.displayName}`
              }
            >
              {docsOpen
                ? 'Close'
                : documents.length === 0
                  ? 'Attach'
                  : `${documents.length} attached`}
            </button>
          )}

          {/*
            The bell.

            Filled when this vehicle carries its own rule for this obligation,
            outline when it does not - so the state is readable before it is
            pressed. A count would be wrong here: there is at most one own rule
            per obligation, by construction.
          */}
          <button
            type="button"
            className={`prow__bell${hasReminder ? ' prow__bell--on' : ''}`}
            onClick={onToggleReminder}
            aria-expanded={reminderOpen}
            aria-label={
              hasReminder
                ? `Edit the reminder for ${detail.displayName}`
                : `Add a reminder for ${detail.displayName}`
            }
            title={
              hasReminder
                ? 'A reminder is set for this obligation'
                : 'Remind somebody before this expires'
            }
          >
            <span aria-hidden="true">{hasReminder ? '🔔' : '🔕'}</span>
          </button>

          <button
            type="button"
            className="prow__edit"
            onClick={onEdit}
            aria-label={`Edit ${detail.displayName}`}
          >
            Edit
          </button>
        </div>
      </div>

      <div className="prow__meta mono">
        <span>
          State <b>{describeStatus(detail.status)}</b>
        </span>
        <span>
          Source <b>{describeSource(detail.source)}</b>
        </span>
        {detail.isManualOverride && <span className="prow__override">hand-corrected</span>}
        {detail.source === Source.RegistryApi && <span className="prow__rto">RTO</span>}
      </div>

      {/*
        The insurer and the policy number.

        These have been on the DTO since the first schema and were rendered nowhere
        — reachable only by opening the compliance editor, which is a form for
        CHANGING a value, not a place to read one. The registry sweep paid for them
        across 588 vehicles and then they sat unread.

        Only shown when present: an empty "Provider —" line on every obligation
        that has no issuer would be five rows of nothing on most vehicles.
      */}
      {(detail.providerName || detail.certificateNumber) && (
        <div className="prow__meta mono">
          {detail.providerName && (
            <span>
              Issued by <b>{detail.providerName}</b>
            </span>
          )}
          {detail.certificateNumber && (
            <span>
              No. <b>{detail.certificateNumber}</b>
            </span>
          )}
        </div>
      )}

      {detail.rawSourceValue && detail.status === Status.Unknown && (
        <div>
          <span className="raw mono">sheet said “{detail.rawSourceValue}”</span>
        </div>
      )}

      {acceptsDocument && (
        <ObligationDocuments
          vehicleId={vehicleId}
          detail={detail}
          documents={documents}
          open={docsOpen}
          onClose={onToggleDocs}
        />
      )}

      {/* Inline, under the obligation it belongs to - not a modal. This codebase
          routes pages and keeps exactly two overlays, and a reminder form is not
          a candidate to be the third. */}
      {reminderEditor}
    </div>
  )
}

/** Stable identity, so `reminders.data ?? EMPTY_RULES` does not defeat useMemo. */
const EMPTY_RULES: ReminderRule[] = []

function describeStatus(status: number | undefined): string {
  switch (status) {
    case Status.Known:
      return 'Known'
    case Status.NoExpiry:
      return 'No expiry'
    case Status.NotApplicable:
      return 'Not applicable'
    default:
      return 'Unknown'
  }
}

/**
 * The sync date, written the way a person reads a date rather than a timestamp.
 *
 * Local time deliberately: the stamp is UTC because that is the only sane thing to
 * store, but a fleet manager in Pune asking "when did this last sync" means their
 * own clock, and a UTC date is off by a day for anything after half past five.
 */
function formatSyncDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * How long ago, in the coarsest unit that is still true.
 *
 * A date alone does not answer the question being asked. "12 Mar 2026" requires the
 * reader to work out whether that is recent; "5 months ago" does not, and the second
 * is what decides how much to trust everything else on the page.
 */
function describeAge(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)

  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`

  const months = Math.floor(days / 30)
  if (months < 12) return months === 1 ? 'a month ago' : `${months} months ago`

  const years = Math.floor(days / 365)
  return years === 1 ? 'over a year ago' : `${years} years ago`
}

function describeSource(source: number | null | undefined): string {
  switch (source) {
    case Source.Manual:
      return 'Manual entry'
    case Source.ExcelImport:
      return 'Excel import'
    case Source.RegistryApi:
      return 'RTO registry'
    default:
      return 'Not recorded'
  }
}

function formatWhen(iso: string | undefined): string {
  if (!iso) return '—'

  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
